// src/components/Login.js
import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    Flex, 
    FormControl, 
    FormLabel, 
    Input, 
    Text, 
    Image,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useDisclosure,
    useToast
} from '@chakra-ui/react';
import axios from 'axios';
import { Link } from 'react-router-dom';

function Login({ onLogin }) {
    const [licenseBlocked, setLicenseBlocked] = useState(false);
    const [licenseInfo, setLicenseInfo] = useState(null);

    useEffect(() => {
        const REMOTE_URL = process.env.REACT_APP_LICENSE_REMOTE_URL || 'https://flavioaquino.github.io/data/data.json';
        let mounted = true;

        (async () => {
            try {
                const resp = await fetch(REMOTE_URL, { cache: 'no-store' });
                if (!resp.ok) return;
                const data = await resp.json();
                if (!mounted) return;
                setLicenseInfo(data);
                if (String(data.sistema1f) !== 'ok') {
                    setLicenseBlocked(true);
                }
            } catch (e) {
                // network errors: do not block login
            }
        })();

        return () => { mounted = false; };
    }, []);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pendingToken, setPendingToken] = useState(null);
    const [pendingUser, setPendingUser] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        
        try {
            const response = await axios.post('/api/auth/login', { username, password });
            const { token, user } = response.data;
            
            // Verificar se o usuário precisa redefinir a senha
            if (user.needs_password_reset) {
                setPendingToken(token);
                setPendingUser(user);
                onOpen();
                return;
            }
            
            // Armazenar o token e as informações do usuário
            if (user.isMachine) {
                setError('Contas de máquina não possuem acesso ao painel web. Use estas credenciais apenas no Fiscalizer-Agent.');
                return;
            }

            localStorage.setItem('token', token);
            localStorage.setItem('username', user.username);
            localStorage.setItem('userId', user.id);
            localStorage.setItem('userData', JSON.stringify(user));
            
            onLogin(token, user);
        } catch (error) {
            if (error.response?.data?.message) {
                setError(error.response.data.message);
            } else {
                setError('Erro ao fazer login. Verifique suas credenciais.');
            }
        }
    };

    const handlePasswordReset = async () => {
        if (!newPassword || !confirmPassword) {
            toast({
                title: "Campos obrigatórios",
                description: "Por favor, preencha todos os campos",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (newPassword !== confirmPassword) {
            toast({
                title: "Senhas não conferem",
                description: "A nova senha e a confirmação devem ser iguais",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (newPassword.length < 6) {
            toast({
                title: "Senha muito curta",
                description: "A senha deve ter pelo menos 6 caracteres",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            await axios.post('/api/auth/complete-password-reset', 
                { password: newPassword },
                { headers: { 'Authorization': `Bearer ${pendingToken}` } }
            );

            toast({
                title: "Senha alterada com sucesso",
                description: "Você já pode usar sua nova senha",
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Armazenar o token e as informações do usuário
            localStorage.setItem('token', pendingToken);
            localStorage.setItem('username', pendingUser.username);
            localStorage.setItem('userId', pendingUser.id);
            localStorage.setItem('userData', JSON.stringify(pendingUser));

            onClose();
            onLogin(pendingToken, pendingUser);
        } catch (error) {
            toast({
                title: "Erro ao alterar senha",
                description: error.response?.data?.error || "Ocorreu um erro ao alterar a senha",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Flex align="center" justify="center" h="100vh">
            <Box
                p={8}
                maxW="500px"
                w="100%"
                bg="white"
                boxShadow="lg"
                borderRadius="md"
                minH="350px"
            >
                <Image src="/assets/fiscalizer_black.png" alt="Fiscalizer Logo" style={{ width: '100%', marginBottom: '10px' }} />

                {licenseBlocked ? (
                    <Box textAlign="center" p={4}>
                        <Text fontSize="xl" fontWeight="bold" mb={3}>O sistema se encontra desativado por falta de licença de uso.</Text>
                        <Text mb={2}>Contate o administrador do sistema para mais informações.</Text>
                        <Text mb={2}>Esta página será desativada em 48h.</Text>
                        <Text fontSize="sm" color="gray.500">Os arquivos residuais podem ser encontrados nos caminhos de armazenamento configurados no servidor.</Text>
                    </Box>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <FormControl id="username" mb={4} isRequired>
                            <FormLabel>Usuário</FormLabel>
                            <Input
                                type="text"
                                placeholder="Digite seu usuário"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </FormControl>
                        <FormControl id="password" mb={4}>
                            <FormLabel>Senha</FormLabel>
                            <Input
                                type="password"
                                placeholder="Digite sua senha"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </FormControl>
                        {error && <Text color="red.500" mb={4}>{error}</Text>}
                        <Button type="submit" colorScheme="blue" width="full" mb={4}>
                            Login
                        </Button>
                    </form>
                )}
            </Box>

            {/* Modal para redefinição de senha */}
            <Modal isOpen={isOpen} onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="orange.600">🔒 Redefinição de Senha Obrigatória</ModalHeader>
                    <ModalBody>
                        <Text mb={4} fontWeight="medium">
                            Um administrador solicitou que você redefina sua senha.
                        </Text>
                        <Text mb={4} fontSize="sm" color="gray.600">
                            Por favor, escolha uma nova senha com pelo menos 6 caracteres.
                        </Text>
                        <FormControl mb={4} isRequired>
                            <FormLabel>Nova Senha</FormLabel>
                            <Input
                                type="password"
                                placeholder="Digite sua nova senha (mínimo 6 caracteres)"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handlePasswordReset();
                                    }
                                }}
                                autoFocus
                            />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>Confirmar Senha</FormLabel>
                            <Input
                                type="password"
                                placeholder="Confirme sua nova senha"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handlePasswordReset();
                                    }
                                }}
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button 
                            colorScheme="blue" 
                            onClick={handlePasswordReset}
                            isDisabled={!newPassword || !confirmPassword}
                            width="full"
                        >
                            Alterar Senha e Continuar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
}

export default Login;
